<script setup lang="ts">
import * as R from 'ramda'
import { computed } from 'vue'

import type { TeamSide } from '@collector/shared-domain'
import type { Coordinates } from '@collector/shared-ui-domain'

import type { ZoneValue } from '../../types'
import { getCoordinateZoneIndex } from '../../utils'

type Props = {
  incidentParticipantTeamSide: TeamSide
  coordinates: Coordinates | null
  teamSidesChanged: boolean
  zonesMatrix: number[][]
}

const props = defineProps<Props>()

// data
/** NOTE: colors are not in Figma pallete */
const colorMap: Partial<Record<ZoneValue, string>> = {
  '1': 'rgb(158,184,158)',
  '2': 'rgb(23,107,21)',
  '3': 'rgb(196,206,197)',
  '4': 'rgb(138,173,139)',
  '5': 'rgb(178,195,178)',
  '6': 'rgb(61,129,60)',
  '7': 'rgb(100,151,99)',
  '8': 'rgb(138,173,139)',
  '9': 'rgb(100,151,99)',
  '10': 'rgb(43,118,41)',
}

// methods
function getCoordinatesZonesMatrixCellIndexes(
  coordinates: Coordinates,
): [number, number] {
  const rowIndex = getCoordinateZoneIndex(
    coordinates.y,
    props.zonesMatrix.length,
  )
  const colIndex = getCoordinateZoneIndex(
    coordinates.x,
    props.zonesMatrix[0].length,
  )
  return [rowIndex, colIndex]
}

// computed
const coordinatesMatrixCellIndexes = computed(() =>
  props.coordinates
    ? getCoordinatesZonesMatrixCellIndexes(props.coordinates)
    : null,
)

/**
 * Reverses the rows of the matrix in a computed property.
 *
 * This avoids using `reverse()` directly in the `v-for` loop,
 * which would execute `reverse()` each time `coordinatesCellIndexes`
 * is recomputed, leading to unnecessary performance costs.
 */
const zonesMatrixWithTeamSide = computed(() => {
  if (props.incidentParticipantTeamSide === 'away') {
    return props.zonesMatrix.map((row) => R.reverse(row))
  }
  return props.zonesMatrix
})
</script>

<template>
  <!-- hardcoded 33 columns (zones on x-axis) -->
  <div
    class="absolute top-0 left-0 grid h-full w-full grid-cols-33 gap-[1px]"
    :class="{
      'scale-x-[-1] scale-y-[-1] transform': teamSidesChanged,
    }"
    data-testid="pitch-field"
  >
    <template
      v-for="(row, rowIndex) in zonesMatrixWithTeamSide"
      :key="rowIndex"
    >
      <template
        v-for="(cell, colIndex) in row"
        :key="colIndex"
      >
        <span
          class="h-full w-full"
          :style="[
            colorMap[cell as ZoneValue]
              ? `background-color: ${colorMap[cell as ZoneValue]};`
              : '',
            { containerType: 'size' },
          ]"
        >
          <span
            v-if="
              coordinatesMatrixCellIndexes &&
              coordinatesMatrixCellIndexes[0] === rowIndex &&
              coordinatesMatrixCellIndexes[1] === colIndex
            "
            class="flex h-full w-full items-center justify-center font-extrabold text-[rgba(0,0,0,1)] select-none"
            :style="{ fontSize: '120cqh' }"
          >
            x
          </span>
        </span>
      </template>
    </template>
  </div>
</template>
